'use client';

import { useState, useEffect } from 'react';
import ProgramCard from './ProgramCard';
import ProgramFilter from './ProgramFilter';
import ProgramComparison from './ProgramComparison';
import ProgramFinder from './ProgramFinder';
import ImpactStats from './ImpactStats';
import Icon from '@/components/ui/AppIcon';
import RecipeSubmissionForm from './RecipeSubmissionForm';

interface Program {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  alt: string;
  outcomes: string[];
  ageRange: string;
  duration: string;
  studentsReached: number;
}

const InitiativesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All Programs');
  const [showComparison, setShowComparison] = useState(false);
  const [showFinder, setShowFinder] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const programs: Program[] = [
  {
    id: 'farms-for-thought',
    title: 'Farms for Thought',
    category: 'Flagship Program',
    description: 'Our comprehensive flagship program that transforms classrooms into innovation labs. Students design, build, and manage hydroponic systems while learning STEM, sustainability, and entrepreneurship. This integrated program combines hydroponics education, technology competencies, and nutrition science into one cohesive learning experience.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cab3becc-1765312684878.png",
    alt: 'Students working together on hydroponic system with sensors and monitoring equipment in modern classroom',
    outcomes: [
    'Build functional hydroponic systems from scratch',
    'Develop technology competencies: engineering, coding, data literacy, environmental monitoring',
    'Understand nutrition science and food systems',
    'Analyze plant growth data using AI dashboards',
    'Hands-on plant science and cross-curricular STEM integration',
    'Connect agriculture to personal wellness and healthy eating habits'],
    ageRange: 'Grades K-12',
    duration: '12-28 weeks',
    studentsReached: 2820
  },
  {
    id: 'technology-development',
    title: 'Technology Development',
    category: 'Technology',
    description: 'Advanced technology development initiatives in partnership with USC, focusing on cutting-edge innovations in agricultural technology, automation, and sustainable food systems research.',
    image: "/assets/images/Screenshot_2026-02-03_at_12.28.07_PM-1770139696130.png",
    alt: 'Students and researchers working with advanced technology equipment in university lab setting',
    outcomes: [
    'Collaborate with USC on agricultural technology research',
    'Develop innovative solutions for sustainable food systems',
    'Advance automation and monitoring technologies',
    'Bridge academic research with practical applications'],
    ageRange: 'High School & University',
    duration: 'Ongoing',
    studentsReached: 0
  },
  {
    id: 'food-justice-policy-tracker',
    title: 'Food Justice Policy Tracker',
    category: 'Community Impact',
    description: 'In collaboration with the RECIPES network, we track current legislation and initiatives that advance equitable food access. Our research team works in collaboration with the RECIPES network, funded by the National Science Foundation. We track current legislation and initiatives that advance equitable food access.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_155fa2fff-1766902837956.png",
    alt: 'Research documents and policy papers spread on desk with laptop showing legislative tracking dashboard',
    outcomes: [
    'Track food justice legislation nationwide',
    'Collaborate with RECIPES network researchers',
    'Advance equitable food access initiatives',
    'Support evidence-based policy development'],

    ageRange: 'All Ages',
    duration: 'Ongoing',
    studentsReached: 0
  },
  {
    id: 'cook-along-series',
    title: 'Cook-Along Series',
    category: 'Community Impact',
    description: 'Our Cook Along Series is one of our signature initiatives. The Cook-Along Series is a way to connect with our community through a shared language: food! We are dedicated to sharing and innovating nutritious and delectable recipes. Most importantly, these recipes are made to work with ingredients being provided at our Grocery Drives or grown in our Farms For Thought program. Soon, the series will be more accessible than ever! Our team has compiled our favorite recipes and created a Cook-Along Cook Book, available as an e-Book soon.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10fce965b-1768913230651.png",
    alt: 'Community members cooking together in bright kitchen with fresh vegetables and herbs from local farm',
    outcomes: [
    'Connect community through shared food experiences',
    'Share nutritious and delectable recipes',
    'Use ingredients from Grocery Drives and Farms',
    'Access upcoming Cook-Along Cook Book e-Book'],

    ageRange: 'All Ages',
    duration: 'Ongoing',
    studentsReached: 0
  },
  {
    id: 'food-drives-sponsored-families',
    title: 'Food Drives',
    category: 'Community Impact',
    description: 'Two of our community initiatives include food drives hosted throughout the year. These programs directly support families in need while building stronger community connections and addressing food insecurity.',
    image: "/assets/images/Screenshot_2026-02-03_at_12.23.33_PM-1770139421914.png",
    alt: 'Community volunteers gathered at outdoor food drive event with tables of fresh produce and food items',
    outcomes: [
    'Host food drives throughout the year',
    'Support families in need with fresh food',
    'Build stronger community connections',
    'Address food insecurity directly'],

    ageRange: 'All Ages',
    duration: 'Ongoing',
    studentsReached: 0
  }];


  const categories = [
  'All Programs',
  'Flagship Program',
  'STEM Education',
  'Technology',
  'Health & Wellness',
  'Community Impact'];


  const filteredPrograms = programs.filter((program) => {
    const categoryMatch =
    selectedCategory === 'All Programs' || program.category === selectedCategory;

    return categoryMatch;
  });

  const handleLearnMore = (id: string) => {
    if (id === 'farms-for-thought') {
      window.location.href = '/farms-for-thought';
    } else if (id === 'food-justice-policy-tracker') {
      window.open('https://wastedfood.american.edu/about-us/#students', '_blank');
    } else if (id === 'cook-along-series') {
      setShowRecipeForm(true);
    } else {
      alert(`Learn more about ${programs.find((p) => p.id === id)?.title}. Full program details coming soon!`);
    }
  };

  const handleFindPrograms = (criteria: {ageGroup: string;interests: string[];}) => {
    alert(`Finding programs for ${criteria.ageGroup} interested in: ${criteria.interests.join(', ')}`);
    setShowFinder(false);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Our Initiatives
            </h1>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Comprehensive programs bridging STEM education, sustainable agriculture, and community empowerment
            </p>
          </div>

          <div className="mb-12">
            <ImpactStats />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-xl p-6 shadow-brand-sm h-64"></div>
            </div>
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                <div key={i} className="bg-card rounded-xl shadow-brand-md h-96"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Our Initiatives
          </h1>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            Comprehensive programs bridging STEM education, sustainable agriculture, and community empowerment through innovative hydroponics and climate tech education
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setShowComparison(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-surface text-brand-primary font-semibold rounded-lg hover:bg-brand-primary hover:text-brand-primary-foreground transition-all duration-300 shadow-brand-sm">

              <Icon name="TableCellsIcon" size={20} variant="outline" />
              <span>Compare Programs</span>
            </button>

            <button
              onClick={() => setShowFinder(!showFinder)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-brand-primary-foreground font-semibold rounded-lg hover:bg-brand-primary/90 transition-all duration-300 shadow-brand-sm">

              <Icon name="MagnifyingGlassIcon" size={20} variant="outline" />
              <span>Find Your Program</span>
            </button>
          </div>
        </div>

        <div className="mb-12">
          <ImpactStats />
        </div>

        {showFinder &&
        <div className="mb-12">
            <ProgramFinder onFindPrograms={handleFindPrograms} />
          </div>
        }

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
          <div className="lg:col-span-1">
            <ProgramFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory} />

          </div>

          <div className="lg:col-span-3">
            {filteredPrograms.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPrograms.map((program) =>
              <ProgramCard
                key={program.id}
                program={program}
                onLearnMore={handleLearnMore} />

              )}
              </div> :

            <div className="bg-surface rounded-xl p-12 text-center">
                <Icon
                name="FaceFrownIcon"
                size={48}
                variant="outline"
                className="text-muted-foreground mx-auto mb-4" />

                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  No Programs Found
                </h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your filters to see more programs
                </p>
                <button
                onClick={() => {
                  setSelectedCategory('All Programs');
                }}
                className="px-6 py-2.5 bg-brand-primary text-brand-primary-foreground font-semibold rounded-lg hover:bg-brand-primary/90 transition-all duration-300">

                  Reset Filters
                </button>
              </div>
            }
          </div>
        </div>

        <div style={{ backgroundColor: '#4F744A' }} className="rounded-xl p-8 md:p-12 text-center text-white shadow-brand-lg">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Bring Innovation to Your School?
          </h2>
          <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
            Partner with us to implement cutting-edge STEM programs that inspire students and create lasting impact in your community.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://forms.gle/JAnAQKxV7VuoBwnGA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-brand-primary font-semibold rounded-lg hover:bg-white/90 transition-all duration-300 shadow-brand-sm">

              <Icon name="UserGroupIcon" size={20} variant="outline" />
              <span>Get Involved</span>
            </a>
          </div>
        </div>
      </div>

      <ProgramComparison
        isVisible={showComparison}
        onClose={() => setShowComparison(false)} />

      {showRecipeForm && (
        <RecipeSubmissionForm onClose={() => setShowRecipeForm(false)} />
      )}
    </div>
  );

};

export default InitiativesInteractive;